import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import { CommiteeIdResolutionStatus, useCommiteeIdResolutionFromUrl } from '@/utils/router';
import { apiDelete, apiPatch, apiPost, apiPut } from './api.utils';
import { type QueryKey, queryClient } from './queries.utils';

export function extractErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data;
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.map((e: { message?: string }) => e.message).join(', ');
    }
    if (data.message) {
      return data.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Erreur inconnue';
}

export enum MutationMethod {
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

interface MutationOptions<T, K extends object, U> {
  url: string | ((data: U) => string);
  method: MutationMethod;
  invalidateQueries?: QueryKey[];
  body?: (data: U) => K;
  onSuccess?: (data: T) => void;
  successMessage?: string | ((data: U) => string);
  /** False when the caller shows the failure itself. */
  toastError?: boolean;
}

interface MutationResult<T, U = void> {
  mutate: (data: U) => Promise<T>;
  mutateAsync: (data: U) => Promise<T>;
  isPending: boolean;
  variables: U | undefined;
  isSuccess: boolean;
}

export function mutation<T, K extends object = never, U = void>(
  options: MutationOptions<T, K, U>
): MutationResult<T, U> {
  const { url, method, invalidateQueries, onSuccess, successMessage, toastError = true } = options;

  const mutation = useMutation({
    mutationFn: async (data: U) => {
      const computedUrl = typeof url === 'function' ? url(data) : url;
      const body = options.body?.(data);
      switch (method) {
        case MutationMethod.POST:
          return apiPost<T>(computedUrl, body);
        case MutationMethod.PUT:
          return apiPut<T>(computedUrl, body);
        case MutationMethod.DELETE:
          return apiDelete<T>(computedUrl);
        case MutationMethod.PATCH:
          return apiPatch<T>(computedUrl, body);
      }
    },
    onSuccess: async (data, variables) => {
      const message =
        typeof successMessage === 'function' ? successMessage(variables) : successMessage;
      toast.success(message ?? 'Opération réussie');
      await onSuccess?.(data);
      await Promise.all(
        (invalidateQueries ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey }))
      );
    },
    onError: (error) => {
      if (!toastError) return;
      toast.error(`L'opération a échoué: ${extractErrorMessage(error)}`);
    },
  });

  return {
    mutate: mutation.mutateAsync,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    variables: mutation.variables,
    isSuccess: mutation.isSuccess,
  };
}

export function commiteeMutation<T, K extends object = never, U = void>(
  options: MutationOptions<T, K, U>
): MutationResult<T, U> {
  const commiteeIdResolution = useCommiteeIdResolutionFromUrl();

  return mutation<T, K, U>({
    ...options,
    url: (data: U) => {
      const computedUrl = typeof options.url === 'function' ? options.url(data) : options.url;
      if (computedUrl.includes(':commiteeId')) {
        if (commiteeIdResolution.status !== CommiteeIdResolutionStatus.READY)
          throw new Error('Commitee ID is not available yet');

        return computedUrl.replace(':commiteeId', commiteeIdResolution.commiteeId.toString());
      }
      return computedUrl;
    },
  });
}
